import React, { useState, useEffect } from 'react';
import { 
    Box, 
    Button, 
    Select, 
    MenuItem, 
    FormControl,
    CircularProgress,
    InputLabel,
    Typography,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Popover,
    TextField
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    FileDownload as FileDownloadIcon,
    CalendarMonth as CalendarIcon
} from '@mui/icons-material';
import auditService from '../../services/auditService';
import { getAllProjectsNoPagination } from '../../services/projectService';
import { getAllUsersNoPagination } from '../../services/userService';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import TablePagination from '../common/TablePagination';
import dayjs from 'dayjs';

// Function to validate date format (MM/DD/YYYY)
const isValidDateFormat = (dateString) => {
    if (!dateString) return true; // Empty is valid (will be caught by required validation)
    
    // Check format using regex (MM/DD/YYYY)
    const regex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;
    if (!regex.test(dateString)) return false;
    
    // Parse the date parts
    const [month, day, year] = dateString.split('/').map(part => parseInt(part, 10));
    
    // Create a date object and check if it's valid
    const date = new Date(year, month - 1, day);
    return (
        date.getFullYear() === year &&
        date.getMonth() === month - 1 &&
        date.getDate() === day
    );
};

const DateInput = ({ label, value, onChange, onCalendarClick, error }) => {
    const handleChange = (e) => {
        const newValue = e.target.value;
        onChange(newValue);
    };
    
    return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <TextField
                label={label}
                value={value}
                onChange={handleChange}
                size="small"
                sx={{ width: 140 }}
                placeholder="MM/DD/YYYY"
                error={error && value && !isValidDateFormat(value)}
                helperText={error && value && !isValidDateFormat(value) ? "Invalid format" : ""}
                InputProps={{
                    endAdornment: (
                        <IconButton 
                            size="small" 
                            onClick={onCalendarClick}
                            sx={{ mr: -1 }}
                        >
                            <CalendarIcon fontSize="small" />
                        </IconButton>
                    )
                }}
            />
        </Box>
    );
};

const AuditLogSearch = ({ onBack }) => {
    // Calendar state
    const [anchorEl, setAnchorEl] = useState(null);
    const [calendarType, setCalendarType] = useState(null);
    
    // Date state
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [dateError, setDateError] = useState('');

    // Filter state
    const [userId, setUserId] = useState('');
    const [eventType, setEventType] = useState('');
    const [projectId, setProjectId] = useState('');
    
    // Data state
    const [projects, setProjects] = useState([]);
    const [users, setUsers] = useState([]);
    const [results, setResults] = useState([]);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [isArchiveSearch, setIsArchiveSearch] = useState(false);
    
    // Pagination state
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [totalCount, setTotalCount] = useState(0);

    // Load projects and users
    useEffect(() => {
        const loadData = async () => {
            try {
                const [projectsData, usersData] = await Promise.all([
                    getAllProjectsNoPagination(),
                    getAllUsersNoPagination()
                ]);

                // Sort projects by name
                const sortedProjects = [...projectsData].sort((a, b) =>
                    a.name.toLowerCase().localeCompare(b.name.toLowerCase())
                );

                // Sort users by name
                const sortedUsers = usersData.sort((a, b) => {
                    const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
                    const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
                    return nameA.localeCompare(nameB);
                });

                setProjects(sortedProjects);
                setUsers(sortedUsers);
            } catch (err) {
                console.error('Error loading data:', err);
                setError('Failed to load projects or users');
            }
        };
        loadData();
    }, []);

    // Effect to trigger search when pagination changes (but only if we have search criteria)
    useEffect(() => {
        if (startDate && endDate && results.length > 0) {
            handleSearch();
        }
    }, [page, rowsPerPage]);

    const handleSearch = async () => {
        try {
            setError('');
            setDateError('');
            setLoading(true);
            setIsArchiveSearch(false);
            
            // Basic date validation
            if (!startDate || !endDate) {
                setError('Please select both start and end dates');
                return;
            }

            // Validate date format
            if (!isValidDateFormat(startDate) || !isValidDateFormat(endDate)) {
                setDateError('Invalid date format. Please use MM/DD/YYYY');
                setError('Please correct the date format (MM/DD/YYYY)');
                return;
            }

            // Parse dates from MM/DD/YYYY format
            const [startMonth, startDay, startYear] = startDate.split('/');
            const [endMonth, endDay, endYear] = endDate.split('/');

            // Format dates as ISO strings
            const startDateISO = new Date(
                parseInt(startYear),
                parseInt(startMonth) - 1,
                parseInt(startDay)
            ).toISOString();

            const endDateISO = new Date(
                parseInt(endYear),
                parseInt(endMonth) - 1,
                parseInt(endDay)
            ).toISOString();

            console.log('Search dates:', { startDateISO, endDateISO });

            // Check if search spans archived data
            const searchStart = new Date(startDateISO);
            const sixMonthsAgo = new Date();
            sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
            
            if (searchStart < sixMonthsAgo) {
                setIsArchiveSearch(true);
            }

            // Get username from selected userId
            const selectedUser = users.find(u => u._id === userId);
            const username = selectedUser ? selectedUser.username : '';

            const response = await auditService.searchLogs({
                startDate: startDateISO,
                endDate: endDateISO,
                username,
                eventType,
                projectId,
                page,
                limit: rowsPerPage
            });

            setResults(response.logs || []);
            setTotalCount(response.pagination?.total || 0);
        } catch (err) {
            console.error('Search error:', err);
            setError(err.message || 'Failed to perform search');
        } finally {
            setLoading(false);
        }
    };

    const handleExport = async () => {
        try {
            setError('');
            setDateError('');
            setLoading(true);
            
            // Basic date validation
            if (!startDate || !endDate) {
                setError('Please select both start and end dates');
                return;
            }

            // Validate date format
            if (!isValidDateFormat(startDate) || !isValidDateFormat(endDate)) {
                setDateError('Invalid date format. Please use MM/DD/YYYY');
                setError('Please correct the date format (MM/DD/YYYY)');
                return;
            }
            
            // Parse dates from MM/DD/YYYY format
            const [startMonth, startDay, startYear] = startDate.split('/');
            const [endMonth, endDay, endYear] = endDate.split('/');

            // Format dates as ISO strings
            const startDateISO = new Date(
                parseInt(startYear),
                parseInt(startMonth) - 1,
                parseInt(startDay)
            ).toISOString();

            const endDateISO = new Date(
                parseInt(endYear),
                parseInt(endMonth) - 1,
                parseInt(endDay)
            ).toISOString();

            console.log('Export dates:', { startDateISO, endDateISO });

            // Get username from selected userId
            const selectedUser = users.find(u => u._id === userId);
            const username = selectedUser ? selectedUser.username : '';

            // Get the logs for export
            const response = await auditService.searchLogs({
                startDate: startDateISO,
                endDate: endDateISO,
                username,
                eventType,
                projectId,
                limit: 1000 // Increased limit for exports
            });

            const logs = response.logs || response; // Handle both new and old response formats

            // Convert logs to CSV format
            const headers = ['Timestamp', 'Username', 'Event Type', 'Project', 'Details'];
            const csvRows = [
                headers.join(','), // Header row
                ...logs.map(row => {
                    const timestamp = dayjs(row.timestamp).format('MM/DD/YYYY HH:mm:ss');
                    const details = typeof row.details === 'object' 
                        ? Object.entries(row.details)
                            .map(([key, value]) => `${key}: ${value}`)
                            .join('; ')
                        : row.details;
                    
                    // Escape fields that might contain commas
                    const fields = [
                        timestamp,
                        row.username,
                        row.eventType,
                        row.projectName || '',
                        `"${details}"` // Wrap details in quotes to handle commas
                    ];
                    
                    return fields.join(',');
                })
            ];
            
            const csvContent = csvRows.join('\n');
            
            // Create and download the CSV file
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'audit_logs.csv');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Export error:', err);
            setError(err.message || 'Failed to export data');
        } finally {
            setLoading(false);
        }
    };

    const handleCalendarClick = (event, type) => {
        setAnchorEl(event.currentTarget);
        setCalendarType(type);
    };

    const handleCalendarClose = () => {
        setAnchorEl(null);
        setCalendarType(null);
    };

    const handleDateSelect = (date) => {
        const selectedDate = dayjs(date);
        const formattedDate = selectedDate.format('MM/DD/YYYY');
        
        if (calendarType === 'start') {
            setStartDate(formattedDate);
        } else {
            setEndDate(formattedDate);
        }
        handleCalendarClose();
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ 
                p: 3,
                '@media (min-width:601px) and (max-width:1199px)': {
                    p: 2, // Moderate padding for tablets
                    paddingTop: 1, // Reduced top padding for tablets
                    paddingBottom: 2, // Normal bottom padding for tablets
                },
                '@media (max-width:600px)': {
                    p: 1, // Much more reduced padding for mobile
                    paddingTop: 0, // No top padding - layout provides spacing
                    paddingBottom: '88px', // Account for bottom navigation
                },
                '@media (max-width:600px) and (orientation: landscape)': {
                    p: 0.5, // Minimal padding in landscape mode
                    paddingTop: 0, // No top padding
                    paddingBottom: 0.5, // Minimal bottom padding
                    height: 'auto', // Allow natural height
                    overflow: 'visible', // Allow content to overflow
                    minHeight: 'auto', // Remove height constraints
                }
            }}>
                {/* Header */}
                <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    mb: 3,
                    '@media (min-width:601px) and (max-width:1199px)': {
                        mb: 2, // Moderate margin for tablets
                    },
                    '@media (max-width:600px)': {
                        mb: 1, // Much more reduced margin for mobile
                    },
                    '@media (max-width:600px) and (orientation: landscape)': {
                        mb: 0.5, // Reduced margin in landscape mode but keep header visible
                    }
                }}>
                    <IconButton onClick={onBack} sx={{ mr: 2 }}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h6">Audit Log Search</Typography>
                </Box>

                {/* Search Form */}
                <Paper sx={{ 
                    p: 2, 
                    mb: 3,
                    '@media (max-width:600px)': {
                        p: 0, // Remove all padding on mobile
                        mb: 1.5,
                    }
                }}>
                    {/* Desktop Layout */}
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        '@media (max-width:1199px)': {
                            display: 'none', // Hide desktop layout on tablets and mobile
                        }
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            {/* Start Date */}
                            <DateInput 
                                label="Start Date" 
                                value={startDate} 
                                onChange={setStartDate}
                                onCalendarClick={(e) => handleCalendarClick(e, 'start')}
                                error={Boolean(dateError)}
                            />

                            {/* End Date */}
                            <DateInput 
                                label="End Date" 
                                value={endDate} 
                                onChange={setEndDate}
                                onCalendarClick={(e) => handleCalendarClick(e, 'end')}
                                error={Boolean(dateError)}
                            />

                            <Divider orientation="vertical" flexItem />

                            {/* Users Dropdown */}
                            <FormControl sx={{ minWidth: 150, maxWidth: 180 }} size="small">
                                <InputLabel>User</InputLabel>
                                <Select
                                    value={userId}
                                    onChange={(e) => setUserId(e.target.value)}
                                    label="User"
                                >
                                    <MenuItem value="">All Users</MenuItem>
                                    {users.map((user) => (
                                        <MenuItem key={user._id} value={user._id}>
                                            {`${user.firstName} ${user.lastName}`}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            {/* Projects Dropdown */}
                            <FormControl sx={{ minWidth: 150, maxWidth: 180 }} size="small">
                                <InputLabel>Project</InputLabel>
                                <Select
                                    value={projectId}
                                    onChange={(e) => setProjectId(e.target.value)}
                                    label="Project"
                                >
                                    <MenuItem value="">All Projects</MenuItem>
                                    {projects.map((project) => (
                                        <MenuItem key={project._id} value={project._id}>
                                            {project.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            {/* Event Type Dropdown */}
                            <FormControl sx={{ minWidth: 150, maxWidth: 180 }} size="small">
                                <InputLabel>Event Type</InputLabel>
                                <Select
                                    value={eventType}
                                    onChange={(e) => setEventType(e.target.value)}
                                    label="Event Type"
                                >
                                    <MenuItem value="">All Events</MenuItem>
                                    <MenuItem value="Timecard Created">Timecard Created</MenuItem>
                                    <MenuItem value="Timecard Edited">Timecard Edited</MenuItem>
                                    <MenuItem value="Timecard Deleted">Timecard Deleted</MenuItem>
                                    <MenuItem value="Timecard Certified">Timecard Certified</MenuItem>
                                    <MenuItem value="Timecard Approved">Timecard Approved</MenuItem>
                                    <MenuItem value="User Login">User Login</MenuItem>
                                    <MenuItem value="User Logout">User Logout</MenuItem>
                                </Select>
                            </FormControl>

                            <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
                                <Button 
                                    variant="contained" 
                                    onClick={handleSearch}
                                >
                                    Find
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={() => {
                                        setStartDate('');
                                        setEndDate('');
                                        setUserId('');
                                        setEventType('');
                                        setProjectId('');
                                        setResults([]);
                                        setError('');
                                        setDateError('');
                                    }}
                                >
                                    Clear
                                </Button>
                            </Box>
                        </Box>

                        <Button
                            variant="outlined"
                            startIcon={<FileDownloadIcon />}
                            disabled={results.length === 0}
                            onClick={handleExport}
                        >
                            Export
                        </Button>
                    </Box>

                    {/* Tablet Portrait Layout */}
                    <Box sx={{ 
                        display: 'none',
                        '@media (min-width:601px) and (max-width:1199px) and (orientation: portrait)': {
                            display: 'block',
                            px: 2, // Add horizontal padding
                            py: 2, // Add vertical padding
                        }
                    }}>
                        {/* Date Fields Row */}
                        <Box sx={{ 
                            display: 'flex', 
                            gap: 2, 
                            mb: 2,
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}>
                            <DateInput 
                                label="Start Date" 
                                value={startDate} 
                                onChange={setStartDate}
                                onCalendarClick={(e) => handleCalendarClick(e, 'start')}
                                error={Boolean(dateError)}
                            />
                            <DateInput 
                                label="End Date" 
                                value={endDate} 
                                onChange={setEndDate}
                                onCalendarClick={(e) => handleCalendarClick(e, 'end')}
                                error={Boolean(dateError)}
                            />
                        </Box>

                        {/* Dropdowns Column - Stack all 3 dropdowns vertically */}
                        <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column',
                            gap: 2, 
                            mb: 2
                        }}>
                            {/* User Dropdown */}
                            <FormControl size="small" sx={{ width: '100%' }}>
                                <InputLabel>User</InputLabel>
                                <Select
                                    value={userId}
                                    onChange={(e) => setUserId(e.target.value)}
                                    label="User"
                                >
                                    <MenuItem value="">All Users</MenuItem>
                                    {users.map((user) => (
                                        <MenuItem key={user._id} value={user._id}>
                                            {`${user.firstName} ${user.lastName}`}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            {/* Project Dropdown */}
                            <FormControl size="small" sx={{ width: '100%' }}>
                                <InputLabel>Project</InputLabel>
                                <Select
                                    value={projectId}
                                    onChange={(e) => setProjectId(e.target.value)}
                                    label="Project"
                                >
                                    <MenuItem value="">All Projects</MenuItem>
                                    {projects.map((project) => (
                                        <MenuItem key={project._id} value={project._id}>
                                            {project.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            {/* Event Type Dropdown */}
                            <FormControl size="small" sx={{ width: '100%' }}>
                                <InputLabel>Event Type</InputLabel>
                                <Select
                                    value={eventType}
                                    onChange={(e) => setEventType(e.target.value)}
                                    label="Event Type"
                                >
                                    <MenuItem value="">All Events</MenuItem>
                                    <MenuItem value="Timecard Created">Timecard Created</MenuItem>
                                    <MenuItem value="Timecard Edited">Timecard Edited</MenuItem>
                                    <MenuItem value="Timecard Deleted">Timecard Deleted</MenuItem>
                                    <MenuItem value="Timecard Certified">Timecard Certified</MenuItem>
                                    <MenuItem value="Timecard Approved">Timecard Approved</MenuItem>
                                    <MenuItem value="User Login">User Login</MenuItem>
                                    <MenuItem value="User Logout">User Logout</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>

                        {/* Buttons Row - All 3 buttons together */}
                        <Box sx={{ 
                            display: 'flex', 
                            gap: 2,
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}>
                            <Button 
                                variant="contained" 
                                onClick={handleSearch}
                                size="medium"
                                sx={{ minWidth: 100 }}
                            >
                                Find
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={() => {
                                    setStartDate('');
                                    setEndDate('');
                                    setUserId('');
                                    setEventType('');
                                    setProjectId('');
                                    setResults([]);
                                    setError('');
                                    setDateError('');
                                }}
                                size="medium"
                                sx={{ minWidth: 100 }}
                            >
                                Clear
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<FileDownloadIcon />}
                                disabled={results.length === 0}
                                onClick={handleExport}
                                size="medium"
                                sx={{ minWidth: 100 }}
                            >
                                Export
                            </Button>
                        </Box>
                    </Box>

                    {/* Tablet Landscape Layout */}
                    <Box sx={{ 
                        display: 'none',
                        '@media (min-width:601px) and (max-width:1199px) and (orientation: landscape)': {
                            display: 'block',
                            px: 2, // Add horizontal padding
                            py: 1.5, // Add vertical padding
                        }
                    }}>
                        {/* Date Fields Row */}
                        <Box sx={{ 
                            display: 'flex', 
                            gap: 2, 
                            mb: 2,
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}>
                            <DateInput 
                                label="Start Date" 
                                value={startDate} 
                                onChange={setStartDate}
                                onCalendarClick={(e) => handleCalendarClick(e, 'start')}
                                error={Boolean(dateError)}
                            />
                            <DateInput 
                                label="End Date" 
                                value={endDate} 
                                onChange={setEndDate}
                                onCalendarClick={(e) => handleCalendarClick(e, 'end')}
                                error={Boolean(dateError)}
                            />
                        </Box>

                        {/* Dropdowns and Buttons Row - All in one row for landscape */}
                        <Box sx={{ 
                            display: 'flex', 
                            gap: 1.5, 
                            mb: 1.5,
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexWrap: 'wrap'
                        }}>
                            {/* User Dropdown */}
                            <FormControl size="small" sx={{ minWidth: 120, maxWidth: 140 }}>
                                <InputLabel>User</InputLabel>
                                <Select
                                    value={userId}
                                    onChange={(e) => setUserId(e.target.value)}
                                    label="User"
                                >
                                    <MenuItem value="">All Users</MenuItem>
                                    {users.map((user) => (
                                        <MenuItem key={user._id} value={user._id}>
                                            {`${user.firstName} ${user.lastName}`}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            {/* Project Dropdown */}
                            <FormControl size="small" sx={{ minWidth: 120, maxWidth: 140 }}>
                                <InputLabel>Project</InputLabel>
                                <Select
                                    value={projectId}
                                    onChange={(e) => setProjectId(e.target.value)}
                                    label="Project"
                                >
                                    <MenuItem value="">All Projects</MenuItem>
                                    {projects.map((project) => (
                                        <MenuItem key={project._id} value={project._id}>
                                            {project.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            {/* Event Type Dropdown */}
                            <FormControl size="small" sx={{ minWidth: 120, maxWidth: 140 }}>
                                <InputLabel>Event Type</InputLabel>
                                <Select
                                    value={eventType}
                                    onChange={(e) => setEventType(e.target.value)}
                                    label="Event Type"
                                >
                                    <MenuItem value="">All Events</MenuItem>
                                    <MenuItem value="Timecard Created">Timecard Created</MenuItem>
                                    <MenuItem value="Timecard Edited">Timecard Edited</MenuItem>
                                    <MenuItem value="Timecard Deleted">Timecard Deleted</MenuItem>
                                    <MenuItem value="Timecard Certified">Timecard Certified</MenuItem>
                                    <MenuItem value="Timecard Approved">Timecard Approved</MenuItem>
                                    <MenuItem value="User Login">User Login</MenuItem>
                                    <MenuItem value="User Logout">User Logout</MenuItem>
                                </Select>
                            </FormControl>

                            {/* Action Buttons */}
                            <Button 
                                variant="contained" 
                                onClick={handleSearch}
                                size="small"
                                sx={{ minWidth: 70 }}
                            >
                                Find
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={() => {
                                    setStartDate('');
                                    setEndDate('');
                                    setUserId('');
                                    setEventType('');
                                    setProjectId('');
                                    setResults([]);
                                    setError('');
                                    setDateError('');
                                }}
                                size="small"
                                sx={{ minWidth: 70 }}
                            >
                                Clear
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<FileDownloadIcon />}
                                disabled={results.length === 0}
                                onClick={handleExport}
                                size="small"
                                sx={{ minWidth: 70 }}
                            >
                                Export
                            </Button>
                        </Box>
                    </Box>

                    {/* Mobile Portrait Layout */}
                    <Box sx={{ 
                        display: 'none',
                        '@media (max-width:600px) and (orientation: portrait)': {
                            display: 'block',
                            px: 1, // Add horizontal padding
                            py: 1.5, // Add vertical padding
                        }
                    }}>
                        {/* Date Fields Row */}
                        <Box sx={{ 
                            display: 'flex', 
                            gap: 1, 
                            mb: 2,
                            '@media (max-width:600px)': {
                                flexDirection: 'row', // Keep dates side by side
                                justifyContent: 'space-between',
                            }
                        }}>
                            <Box sx={{ flex: 1, mr: 0.5 }}>
                                <DateInput 
                                    label="Start Date" 
                                    value={startDate} 
                                    onChange={setStartDate}
                                    onCalendarClick={(e) => handleCalendarClick(e, 'start')}
                                    error={Boolean(dateError)}
                                />
                            </Box>
                            <Box sx={{ flex: 1, ml: 0.5 }}>
                                <DateInput 
                                    label="End Date" 
                                    value={endDate} 
                                    onChange={setEndDate}
                                    onCalendarClick={(e) => handleCalendarClick(e, 'end')}
                                    error={Boolean(dateError)}
                                />
                            </Box>
                        </Box>

                        {/* Dropdowns Row */}
                        <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column',
                            gap: 1.5, 
                            mb: 2
                        }}>
                            {/* User Dropdown */}
                            <FormControl size="small" sx={{ width: '100%' }}>
                                <InputLabel>User</InputLabel>
                                <Select
                                    value={userId}
                                    onChange={(e) => setUserId(e.target.value)}
                                    label="User"
                                >
                                    <MenuItem value="">All Users</MenuItem>
                                    {users.map((user) => (
                                        <MenuItem key={user._id} value={user._id}>
                                            {`${user.firstName} ${user.lastName}`}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            {/* Project Dropdown */}
                            <FormControl size="small" sx={{ width: '100%' }}>
                                <InputLabel>Project</InputLabel>
                                <Select
                                    value={projectId}
                                    onChange={(e) => setProjectId(e.target.value)}
                                    label="Project"
                                >
                                    <MenuItem value="">All Projects</MenuItem>
                                    {projects.map((project) => (
                                        <MenuItem key={project._id} value={project._id}>
                                            {project.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            {/* Event Type Dropdown */}
                            <FormControl size="small" sx={{ width: '100%' }}>
                                <InputLabel>Event Type</InputLabel>
                                <Select
                                    value={eventType}
                                    onChange={(e) => setEventType(e.target.value)}
                                    label="Event Type"
                                >
                                    <MenuItem value="">All Events</MenuItem>
                                    <MenuItem value="Timecard Created">Timecard Created</MenuItem>
                                    <MenuItem value="Timecard Edited">Timecard Edited</MenuItem>
                                    <MenuItem value="Timecard Deleted">Timecard Deleted</MenuItem>
                                    <MenuItem value="Timecard Certified">Timecard Certified</MenuItem>
                                    <MenuItem value="Timecard Approved">Timecard Approved</MenuItem>
                                    <MenuItem value="User Login">User Login</MenuItem>
                                    <MenuItem value="User Logout">User Logout</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>

                        {/* Buttons Row */}
                        <Box sx={{ 
                            display: 'flex', 
                            gap: 1,
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <Box sx={{ display: 'flex', gap: 1, flex: 1 }}>
                                <Button 
                                    variant="contained" 
                                    onClick={handleSearch}
                                    size="small"
                                    sx={{ flex: 1 }}
                                >
                                    Find
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={() => {
                                        setStartDate('');
                                        setEndDate('');
                                        setUserId('');
                                        setEventType('');
                                        setProjectId('');
                                        setResults([]);
                                        setError('');
                                        setDateError('');
                                    }}
                                    size="small"
                                    sx={{ flex: 1 }}
                                >
                                    Clear
                                </Button>
                            </Box>
                            
                            {/* Export Button - Icon Only for Mobile */}
                            <Button
                                variant="outlined"
                                disabled={results.length === 0}
                                onClick={handleExport}
                                size="small"
                                sx={{
                                    minWidth: 'auto',
                                    padding: '6px',
                                    '& .MuiSvgIcon-root': {
                                        fontSize: '1rem',
                                    }
                                }}
                            >
                                <FileDownloadIcon />
                            </Button>
                        </Box>
                    </Box>

                    {/* Mobile Landscape Layout */}
                    <Box sx={{ 
                        display: 'none',
                        '@media (max-width:600px) and (orientation: landscape)': {
                            display: 'block',
                            px: 1, // Add horizontal padding
                            py: 1, // Add vertical padding
                        }
                    }}>
                        {/* Date Fields Row */}
                        <Box sx={{ 
                            display: 'flex', 
                            gap: 2, 
                            mb: 1.5,
                            justifyContent: 'center', // Center the date fields
                            alignItems: 'center',
                        }}>
                            <DateInput 
                                label="Start Date" 
                                value={startDate} 
                                onChange={setStartDate}
                                onCalendarClick={(e) => handleCalendarClick(e, 'start')}
                                error={Boolean(dateError)}
                            />
                            <DateInput 
                                label="End Date" 
                                value={endDate} 
                                onChange={setEndDate}
                                onCalendarClick={(e) => handleCalendarClick(e, 'end')}
                                error={Boolean(dateError)}
                            />
                        </Box>

                        {/* Dropdowns Row - 3 dropdowns side by side */}
                        <Box sx={{ 
                            display: 'flex', 
                            gap: 1, 
                            mb: 1.5
                        }}>
                            {/* User Dropdown */}
                            <FormControl size="small" sx={{ flex: 1 }}>
                                <InputLabel>User</InputLabel>
                                <Select
                                    value={userId}
                                    onChange={(e) => setUserId(e.target.value)}
                                    label="User"
                                >
                                    <MenuItem value="">All Users</MenuItem>
                                    {users.map((user) => (
                                        <MenuItem key={user._id} value={user._id}>
                                            {`${user.firstName} ${user.lastName}`}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            {/* Project Dropdown */}
                            <FormControl size="small" sx={{ flex: 1 }}>
                                <InputLabel>Project</InputLabel>
                                <Select
                                    value={projectId}
                                    onChange={(e) => setProjectId(e.target.value)}
                                    label="Project"
                                >
                                    <MenuItem value="">All Projects</MenuItem>
                                    {projects.map((project) => (
                                        <MenuItem key={project._id} value={project._id}>
                                            {project.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            {/* Event Type Dropdown */}
                            <FormControl size="small" sx={{ flex: 1 }}>
                                <InputLabel>Event Type</InputLabel>
                                <Select
                                    value={eventType}
                                    onChange={(e) => setEventType(e.target.value)}
                                    label="Event Type"
                                >
                                    <MenuItem value="">All Events</MenuItem>
                                    <MenuItem value="Timecard Created">Timecard Created</MenuItem>
                                    <MenuItem value="Timecard Edited">Timecard Edited</MenuItem>
                                    <MenuItem value="Timecard Deleted">Timecard Deleted</MenuItem>
                                    <MenuItem value="Timecard Certified">Timecard Certified</MenuItem>
                                    <MenuItem value="Timecard Approved">Timecard Approved</MenuItem>
                                    <MenuItem value="User Login">User Login</MenuItem>
                                    <MenuItem value="User Logout">User Logout</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>

                        {/* Buttons Row */}
                        <Box sx={{ 
                            display: 'flex', 
                            gap: 1,
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <Box sx={{ display: 'flex', gap: 1, flex: 1 }}>
                                <Button 
                                    variant="contained" 
                                    onClick={handleSearch}
                                    size="small"
                                    sx={{ flex: 1 }}
                                >
                                    Find
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={() => {
                                        setStartDate('');
                                        setEndDate('');
                                        setUserId('');
                                        setEventType('');
                                        setProjectId('');
                                        setResults([]);
                                        setError('');
                                        setDateError('');
                                    }}
                                    size="small"
                                    sx={{ flex: 1 }}
                                >
                                    Clear
                                </Button>
                            </Box>
                            
                            {/* Export Button - Icon Only for Mobile Landscape */}
                            <Button
                                variant="outlined"
                                disabled={results.length === 0}
                                onClick={handleExport}
                                size="small"
                                sx={{
                                    minWidth: 'auto',
                                    padding: '6px',
                                    '& .MuiSvgIcon-root': {
                                        fontSize: '1rem',
                                    }
                                }}
                            >
                                <FileDownloadIcon />
                            </Button>
                        </Box>
                    </Box>
                </Paper>

                {error && (
                    <Typography color="error" sx={{ mb: 2 }}>
                        {error}
                    </Typography>
                )}

                {/* Archive Search Warning */}
                {isArchiveSearch && (
                    <Paper sx={{ p: 2, mb: 2, bgcolor: 'warning.light' }}>
                        <Typography variant="body1" color="warning.dark">
                            Warning: This search includes archived data and may take longer to complete.
                        </Typography>
                    </Paper>
                )}

                {/* Loading Indicator */}
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                        <CircularProgress />
                    </Box>
                )}

                {results.length > 0 && !loading && (
                    <TableContainer component={Paper} sx={{ mb: 2 }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Timestamp</TableCell>
                                    <TableCell>Username</TableCell>
                                    <TableCell>Event Type</TableCell>
                                    <TableCell>Project</TableCell>
                                    <TableCell>Details</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {results.map((row) => (
                                    <TableRow key={row._id}>
                                        <TableCell>{dayjs(row.timestamp).format('MM/DD/YYYY HH:mm:ss')}</TableCell>
                                        <TableCell>{row.username}</TableCell>
                                        <TableCell>{row.eventType}</TableCell>
                                        <TableCell>{row.projectName}</TableCell>
                                        <TableCell>
                                            <Box sx={{ whiteSpace: 'pre-line' }}>
                                                {typeof row.details === 'object' 
                                                    ? Object.entries(row.details).map(([key, value]) => (
                                                        <div key={key}>
                                                            <strong>{key}:</strong> {value}
                                                        </div>
                                                    ))
                                                    : row.details
                                                }
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                            <TablePagination
                                page={page}
                                rowsPerPage={rowsPerPage}
                                totalCount={totalCount}
                                onPageChange={(newPage) => {
                                    setPage(newPage);
                                }}
                                onRowsPerPageChange={(newRowsPerPage) => {
                                    setRowsPerPage(newRowsPerPage);
                                    setPage(1);
                                }}
                            />
                        </Box>
                    </TableContainer>
                )}

                <Popover
                    open={Boolean(anchorEl)}
                    anchorEl={anchorEl}
                    onClose={handleCalendarClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                >
                    <DateCalendar 
                        onChange={handleDateSelect}
                        showDaysOutsideCurrentMonth
                        firstDayOfWeek={0}
                    />
                </Popover>
            </Box>
        </LocalizationProvider>
    );
};

export default AuditLogSearch;
